import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, CheckCircle2, ArrowLeft, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Invalid reset link");
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 chars"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password/reset", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10" data-testid="reset-page">
      <div className="w-full max-w-md glass-strong rounded-3xl p-8 sm:p-10">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
        {done ? (
          <div className="text-center" data-testid="reset-done">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#32D74B]/20 border border-[#32D74B]/40 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#32D74B]" />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight">Password updated</h1>
            <p className="text-zinc-400 text-sm mt-3">Redirecting you to login...</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-7">
              <div className="w-12 h-12 mx-auto rounded-xl bg-[#E50914]/15 border border-[#E50914]/40 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-[#E50914]" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tight">Reset password</h1>
              <p className="text-zinc-400 text-sm mt-2">Choose a new password for your account.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  data-testid="reset-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New password (min 6 chars)"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  data-testid="reset-confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-[#E50914]/50 text-sm outline-none"
                />
              </div>
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#E50914] hover:bg-[#F6121D] text-white font-bold disabled:opacity-50"
              >
                {busy ? "Saving..." : "Set new password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
